import * as React from "react";
import { observer } from "mobx-react";
import { Toolbar, ToolbarGroup } from "@microsoft/shifts-ui-core";
import FilterableCommandBarItemV2, { DisplayOption } from "./FilterableCommandBarItemV2";
import classNames from "classnames/bind";

export interface FilterableCommandBarV2Props {
    /** Items to be displayed as toolbar items */
    items: FilterableCommandBarItemV2[];

    /** Additional className for the toolbar */
    className?: string;

    /** Additional style properties */
    style?: Record<"toolbar" | "toolbarGroup", string>;

    /** Aria label for the toolbar */
    ariaLabel?: string;

    /** Size of toolbar */
    size?: "small" | "medium" | "large";
}

/**
 * FluentUI 2 Toolbar implementation that replaces legacy CommandBar.
 * Accepts FilterableCommandBarItemV2 instances and renders them using
 * FluentUI 2 Toolbar with proper left/right positioning.
 */
const FilterableCommandBarV2: React.FC<FilterableCommandBarV2Props> = observer(({
    items = [],
    className,
    size,
    style,
    ariaLabel = "Command toolbar"
}) => {
    // Filter items by display option for proper positioning
    const leftItems = React.useMemo(() =>
        items.filter(item => item.displayOption !== DisplayOption.Right),
        [items]
    );

    const rightItems = React.useMemo(() =>
        items.filter(item => item.displayOption === DisplayOption.Right),
        [items]
    );

    return (
        <Toolbar
            aria-label={ariaLabel}
            size={size}
            className={classNames(style?.toolbar, className)}
        >
            {/* Left-aligned items */}
            {leftItems.length > 0 && (
                <ToolbarGroup role="presentation" className={style?.toolbarGroup}>
                    {leftItems.map(item => item.toToolbarItem())}
                </ToolbarGroup>
            )}

            {/* Right-aligned items with auto margin */}
            {rightItems.length > 0 && (
                <ToolbarGroup role="presentation" className={style?.toolbarGroup}>
                    {rightItems.map(item => item.toToolbarItem())}
                </ToolbarGroup>
            )}
        </Toolbar>
    );
});

export default FilterableCommandBarV2;
